import { safeParseJSON } from '@immediate_media/script-utils';
import { getAdSlotsOnPage } from './dom.js';
import { defineResponsiveSizes, isFixedOrMultiSize } from './gpt/sizes.js';
import { debugMessage } from './debug.js';
import { getGlobalSettings } from './settings.js';
import { logger } from './logger.js';
import { getSlots } from './api/slot.js';
// TODO: This should be later replaced with config from ad configuration service
import adSlotsConfig from '../ad-slots-config.json';

import './typedef.js';

/**
 * @typedef {Object} AddSlotParam
 * @property {string} key slot key to match the id from config
 * @property {number} index number of the slot with given key
 */

/**
 * Get a defined ad slot by ID, if any.
 */
export const getDefinedAdSlot = (id) => {
  const existingSlots = googletag.pubads().getSlots();

  return existingSlots.find((slot) => id === slot.getSlotElementId());
};

/**
 * Check if sizes have proper format.
 */
export const validateSizes = (sizes) => {
  if (!Array.isArray(sizes)) {
    throw new Error(`Attempted to register an ad slot with invalid sizes: ${sizes}`);
  }
};

/**
 * Avoid trying to add a slot with the same ID twice.
 */
export const slotExists = (id) => {
  const existingSlot = getDefinedAdSlot(id);

  if (existingSlot) {
    debugMessage(`A slot with id ${id} is already defined`, { slot: existingSlot });

    return true;
  }

  return false;
};

/**
 * Get final targets for ad slot.
 */
const getFinalTargets = (globalSettings, targets, isOutOfPageSlot) => {
  const finalTargets = {
    ...(globalSettings.targets || {}),
    ...(targets || {}),
  };

  if (isOutOfPageSlot) {
    finalTargets.pos = [''];
  }

  return finalTargets;
};

/**
 * Define ad slot within GPT and assign sizes.
 */
const defineAdSlotWithinGpt = ({
  id, unitName, globalSettings, isOutOfPageSlot, sizes,
}) => {
  const nonResponsive = isFixedOrMultiSize(sizes);
  const initialSizes = nonResponsive ? sizes : [0, 0];
  const actualUnitName = unitName || globalSettings.unitName;
  const slot = isOutOfPageSlot
    ? googletag.defineOutOfPageSlot(actualUnitName, id)
    : googletag.defineSlot(actualUnitName, initialSizes, id);

  if (!slot) {
    throw new Error(`Failed to define slot for element with id "${id}"`);
  }

  if (!nonResponsive) {
    defineResponsiveSizes(slot, sizes);
  }

  return slot;
};

/**
 * Define an ad slot.
 */
export const defineAdSlot = ({
  id,
  sizes,
  targets,
  unitName,
}) => {
  const globalSettings = getGlobalSettings();
  const isOutOfPageSlot = !targets?.pos?.length;
  const finalTargets = getFinalTargets(globalSettings, targets, isOutOfPageSlot);

  if (slotExists(id)) return null;
  validateSizes(sizes);
  debugMessage('Defining ad slot', {
    id,
    sizes,
    targets: finalTargets,
    isOutOfPageSlot,
  });

  const slot = defineAdSlotWithinGpt({
    id, unitName, globalSettings, isOutOfPageSlot, sizes,
  });

  slot
    .addService(googletag.pubads())
    .updateTargetingFromMap(finalTargets);

  return slot;
};

/**
 * Define an ad slot based on the slot config.
 * @param {SlotConfig} slotConfig
 */
export const convertToSlot = (slotConfig) => {
  try {
    return slotConfig && defineAdSlot(slotConfig);
  } catch (e) {
    logger.error(e);
    return null;
  }
};

/**
 * Enrich slot definitions with properties like sizes,
 * targets etc. taken from the ad config file.
 * @param {Array<SlotConfig>} allSlotsConfig additional slots properties (sizes, targets etc.)
 * associated with slot keys
 * @param {AddSlotParam} config object containing slot key and index
 * @returns {SlotConfig} ad slot config
 */
const enrichWithConfig = (allSlotsConfig) => (slot) => {
  const currentSlotConfig = allSlotsConfig
    .find((slotConfig) => slotConfig.id === slot.key);

  if (!currentSlotConfig) {
    logger.error(`No ad slot config found for key "${slot.key}" (element id "${slot.key}-${slot.index}")`);
    return null;
  }

  return {
    id: `${slot.key}-${slot.index}`,
    targets: currentSlotConfig.adTargets,
    sizes: safeParseJSON(currentSlotConfig.adSizes) || [],
    unitName: currentSlotConfig.adUnitName,
  };
};

/**
 * Get proper configuration for the ad slots - from ad config file
 * when slots are added via addSlot method in ad queue
 * otherwise from HTML DOM elements.
 */
const getSlotConfigs = () => {
  const domSlots = getAdSlotsOnPage();
  const queueSlots = getSlots();

  return [
    ...queueSlots.map(enrichWithConfig(adSlotsConfig)),
    ...domSlots,
  ];
};

/**
 * Define all ad slots currently on the page.
 */
export const defineAdSlots = () => {
  const slotConfigs = getSlotConfigs();
  const slots = slotConfigs.map(convertToSlot);

  return slots.filter((x) => x); // filter any slots we failed to define
};
